import { env } from 'env';
/**
 * Deals Service
 *
 * Frontend service for unified deal-RPU search API
 * Provides search, validation, and lookup functions for deal_id management
 */

const API_BASE_URL = env.API_URL || 'http://localhost:5001';

export interface UnifiedDeal {
  rpu: string;
  deal_id: string | null;
  deal_name: string | null;
  client_full_name: string | null;
  data_source: 'hubspot' | 'niko_engine';
  // Additional fields from HubSpot
  rpu_nuevo?: string;
  dealID?: string;
  dealName?: string;
  clientFullName?: string;
}

export interface SmartSearchResponse {
  query: string;
  query_type: 'rpu' | 'deal_id' | 'deal_name' | 'numeric' | 'empty';
  found: boolean;
  result: UnifiedDeal | null;
}

export interface SearchResponse {
  query: string;
  results: UnifiedDeal[];
  count: number;
  limit: number;
}

/**
 * Smart search that auto-detects query type (RPU, deal_id, or deal_name)
 */
export async function smartSearchDeal(query: string): Promise<SmartSearchResponse> {
  if (!query || !query.trim()) {
    return {
      query: '',
      query_type: 'empty',
      found: false,
      result: null,
    };
  }

  const response = await fetch(
    `${API_BASE_URL}/api/deals/smart?q=${encodeURIComponent(query.trim())}`
  );

  if (!response.ok) {
    throw new Error(`Smart search failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Find deal by RPU number
 */
export async function findDealByRPU(rpu: string): Promise<UnifiedDeal | null> {
  const response = await fetch(`${API_BASE_URL}/api/deals/rpu/${rpu}`);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`RPU lookup failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Find deal by deal_id
 */
export async function findDealByDealId(dealId: string): Promise<UnifiedDeal | null> {
  const response = await fetch(`${API_BASE_URL}/api/deals/deal/${dealId}`);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Deal ID lookup failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * General search across all fields (RPU, deal_id, deal_name, client_full_name)
 */
export async function searchDeals(query: string, limit: number = 50): Promise<SearchResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/deals/search?q=${encodeURIComponent(query)}&limit=${limit}`
  );

  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Validate and auto-correct deal_id
 *
 * For mock deal_ids (e.g., "DEAL_000101"), searches for the real deal_id
 * Returns corrected deal_id or null if not found
 */
export async function validateAndCorrectDealId(
  dealId: string,
  rpu: string
): Promise<{
  valid: boolean;
  corrected_deal_id: string | null;
  suggestion: string | null;
  message: string;
}> {
  try {
    // Check if it's a mock deal_id (format: DEAL_XXXXXX)
    const isMockDealId = /^DEAL_\d+$/i.test(dealId);

    if (isMockDealId) {
      // Mock deal_id detected, search for real deal_id by RPU
      const deal = await findDealByRPU(rpu);

      if (deal && deal.deal_id) {
        return {
          valid: false,
          corrected_deal_id: deal.deal_id,
          suggestion: deal.deal_id,
          message: `Mock deal_id detected. Real deal_id found: ${deal.deal_id}`,
        };
      } else {
        return {
          valid: false,
          corrected_deal_id: null,
          suggestion: null,
          message: 'Mock deal_id detected, but no real deal_id found for this RPU',
        };
      }
    }

    // Not a mock, validate the deal_id exists
    const deal = await findDealByDealId(dealId);

    if (deal) {
      // Check if the deal_id matches the RPU
      if (deal.rpu === rpu || deal.rpu_nuevo === rpu) {
        return {
          valid: true,
          corrected_deal_id: dealId,
          suggestion: null,
          message: 'Deal ID is valid and matches RPU',
        };
      } else {
        // Deal exists but doesn't match the RPU - flag it
        return {
          valid: false,
          corrected_deal_id: null,
          suggestion: null,
          message: `Deal ID exists but is associated with different RPU: ${deal.rpu}`,
        };
      }
    } else {
      // Deal ID not found, search by RPU to suggest correct one
      const rpuDeal = await findDealByRPU(rpu);

      if (rpuDeal && rpuDeal.deal_id) {
        return {
          valid: false,
          corrected_deal_id: null,
          suggestion: rpuDeal.deal_id,
          message: `Deal ID not found. Suggested deal_id for RPU: ${rpuDeal.deal_id}`,
        };
      } else {
        return {
          valid: false,
          corrected_deal_id: null,
          suggestion: null,
          message: 'Deal ID not found and no deal found for this RPU',
        };
      }
    }
  } catch (error: any) {
    return {
      valid: false,
      corrected_deal_id: null,
      suggestion: null,
      message: `Validation failed: ${error.message}`,
    };
  }
}

/**
 * Calculator run option from FullCalculatorData
 */
export interface CalculatorRunOption {
  module_power_watts: number;
  final_module_count: number;
  system_size_kw: number;
  analysis_date: string;
  label: string;
}

/**
 * Fetch calculator run options for an RPU
 * Returns available module configurations from Niko Engine calculator runs
 */
export async function getCalculatorOptions(rpu: string): Promise<{
  success: boolean;
  rpu: string;
  options: CalculatorRunOption[];
  count: number;
}> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/deals/calculator/options/${rpu}`
    );

    if (!response.ok) {
      if (response.status === 404) {
        return { success: true, rpu, options: [], count: 0 };
      }
      throw new Error(`Calculator options fetch failed: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      success: true,
      rpu,
      options: data.options || [],
      count: data.count || 0
    };
  } catch (error: any) {
    console.error('Error fetching calculator options:', error);
    return { success: false, rpu, options: [], count: 0 };
  }
}

/**
 * Check if RPU has multiple deal_ids (should flag as error)
 *
 * Note: Current API deduplicates by RPU, so this may need backend enhancement
 */
export async function checkForMultipleDealIds(rpu: string): Promise<{
  has_multiple: boolean;
  deal_ids: string[];
  message: string;
}> {
  try {
    // Search for the RPU across all sources
    const searchResults = await searchDeals(rpu, 10);

    // Filter results that match this RPU
    const matchingDeals = searchResults.results.filter(
      (deal) => deal.rpu === rpu || deal.rpu_nuevo === rpu
    );

    // Extract unique deal_ids
    const uniqueDealIds = Array.from(
      new Set(
        matchingDeals
          .map((deal) => deal.deal_id)
          .filter((id): id is string => id !== null && id !== undefined)
      )
    );

    if (uniqueDealIds.length > 1) {
      return {
        has_multiple: true,
        deal_ids: uniqueDealIds,
        message: `WARNING: RPU ${rpu} has multiple deal_ids: ${uniqueDealIds.join(', ')}`,
      };
    } else if (uniqueDealIds.length === 1) {
      return {
        has_multiple: false,
        deal_ids: uniqueDealIds,
        message: 'RPU has single deal_id',
      };
    } else {
      return {
        has_multiple: false,
        deal_ids: [],
        message: 'No deal_id found for RPU',
      };
    }
  } catch (error: any) {
    return {
      has_multiple: false,
      deal_ids: [],
      message: `Check failed: ${error.message}`,
    };
  }
}
